import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { facadeCalc } from './facade';
import { qtyOf, builtQty } from './drawStyles';
import { vatSplit } from './vat';
import { money, locale } from './units';
import { clean, download, safeName } from './exporters';
import { APP_INFO } from './license';
import { companyLine } from './company';
import { materialRows } from './materialList';
import { computePit } from './excavation';

const nf = (v, d = 2) => (+v || 0).toLocaleString(locale(), { minimumFractionDigits: d, maximumFractionDigits: d });
export const lvNum = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
export const oz = (si, pi) => `${String(si + 1).padStart(2, '0')}.${String(pi + 1).padStart(3, '0')}`;
export const lvAmount = (p) => lvNum(p.qty) * lvNum(p.price);

// quantities that can be taken over into a position: facade take-offs of the section + studio drawings
export function lvSources(project, report, t) {
  const out = [];
  (project.facades || []).filter((f) => !f.repId || f.repId === report.id).forEach((f) => {
    const r = f.out || facadeCalc(f, f.opts);
    if (!r) return;
    [['wall', r.wall, 'm2'], ['paintA', r.paintA, 'm2'], ['gross', r.gross, 'm2'], ['reveal', r.reveal, 'm2'], ['lfm', r.lfm, 'lfm'], ['sA', r.sA, 'm2']]
      .filter(([, v]) => v > 0).forEach(([k, v, u]) => out.push({ id: `${f.id}:${k}`, label: `${f.name} – ${t['lvSrc_' + k]}: ${nf(v)} ${t['unit_' + u]}`, qty: Math.round(v * 100) / 100, unit: u }));
  });
  (project.drawings || []).filter((d) => d.kind === 'line' || d.kind === 'area').forEach((d, i) => {
    const v = d.status === 'built' ? builtQty(d) : qtyOf(d), u = d.kind === 'area' ? 'm2' : 'lfm';
    if (v > 0) out.push({ id: `dr:${d.id}`, label: `${t.lvDrawing} ${i + 1} – ${t['dsSt_' + d.style] || d.style}: ${nf(v)} ${t['unit_' + u]}`, qty: Math.round(v * 100) / 100, unit: u });
  });
  // civil works: pipes (lfm) and parts (pcs) per utility with project prices, pits as lump sums
  if (project.pipes?.length || project.fittings?.length || project.pits?.length) {
    materialRows(project).rows.filter((r) => r.qty > 0).forEach((r) => {
      const u = r.unit === 'lfm' ? 'lfm' : r.unit === 'm' ? 'lfm' : 'pcs', q = Math.round(r.qty * 100) / 100;
      const unitCost = r.price != null || r.install != null ? (+r.price || 0) + (+r.install || 0) : null;
      out.push({ id: `net:${r.key}`, label: `${r.type && t['type_' + r.type] ? `${t['type_' + r.type]} – ` : ''}${r.label}: ${nf(q)} ${t['unit_' + u]}`, text: `${r.type && t['type_' + r.type] ? `${t['type_' + r.type]} – ` : ''}${r.label}`, qty: q, unit: u, price: unitCost });
    });
    (project.pits || []).forEach((p, i) => {
      const c = computePit(p);
      out.push({ id: `pit:${p.id}`, label: `${p.name || `${t.pitTool} ${i + 1}`} – ${t.pitCostTitle}: ${nf(c.excav.m3)} m³`, text: `${p.name || `${t.pitTool} ${i + 1}`} – ${t.pitCostTitle} (${nf(c.area)} m², ${t.pitDepth} ${nf(c.depth)} m)`, qty: 1, unit: 'lump', price: c.priced ? Math.round(c.total * 100) / 100 : null });
    });
  }
  return out;
}

const sections = (project) => project.reports.map((r, si) => ({ r, si, list: r.lv || [] })).filter((s) => s.list.length);
export const lvTotal = (project) => sections(project).reduce((a, s) => a + s.list.reduce((b, p) => b + lvAmount(p), 0), 0);

export function exportLvXLSX(project, t) {
  const rows = [[`${t.lvTitle} – ${project.title}`], [companyLine()], [], [t.lvOz, t.lvText, t.rmQty, t.mfColUnit, t.lvEp, t.lvGp]];
  sections(project).forEach(({ r, si, list }) => {
    rows.push([String(si + 1).padStart(2, '0'), r.title]);
    list.forEach((p, pi) => rows.push([oz(si, pi), p.text, lvNum(p.qty), t['unit_' + p.unit] || p.unit, lvNum(p.price), Math.round(lvAmount(p) * 100) / 100]));
    rows.push(['', `${t.lvSecSum} ${String(si + 1).padStart(2, '0')}`, '', '', '', Math.round(list.reduce((a, p) => a + lvAmount(p), 0) * 100) / 100], []);
  });
  const v = vatSplit(lvTotal(project));
  rows.push(['', t.vatNet, '', '', '', Math.round(v.net * 100) / 100], ['', t.vatLine.replace('{rate}', v.rate), '', '', '', Math.round(v.vat * 100) / 100], ['', t.vatGross, '', '', '', Math.round(v.gross * 100) / 100]);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [9, 60, 10, 10, 12, 14].map((wch) => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, t.lvSheet.slice(0, 31));
  download(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${safeName(project.title)}_LV.xlsx`);
}

export function exportLvPdf(project, t) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' }), M = 14, X = [M, 34, 138, 141, 172, 196], R = [false, false, true, false, true, true];
  let y = 0;
  const head = () => {
    doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 22, 'F');
    doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.text(clean(`${t.lvTitle} – ${project.title}`), M, 10);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.text(clean([companyLine(), new Date().toLocaleDateString(locale())].filter(Boolean).join(' · ')), M, 17);
    doc.setTextColor(20); y = 30;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
    [t.lvOz, t.lvText, t.rmQty, t.mfColUnit, t.lvEp, t.lvGp].forEach((h, i) => doc.text(clean(h), X[i], y, { align: R[i] ? 'right' : 'left' }));
    doc.setDrawColor(148, 163, 184); doc.line(M, y + 1.5, 196, y + 1.5); y += 7;
  };
  const ensure = (h) => { if (y + h > 280) { doc.addPage(); head(); } };
  const right = (s, x) => doc.text(clean(s), x, y, { align: 'right' });
  head();
  sections(project).forEach(({ r, si, list }) => {
    ensure(14); doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.text(clean(`${String(si + 1).padStart(2, '0')}  ${r.title}`), M, y); y += 6;
    doc.setFontSize(9);
    list.forEach((p, pi) => {
      const ln = doc.splitTextToSize(clean(p.text || ''), 88);
      ensure(ln.length * 4.2 + 3); doc.setFont('helvetica', 'normal');
      doc.text(oz(si, pi), X[0], y); doc.text(ln, X[1], y);
      right(nf(lvNum(p.qty)), X[2]); doc.text(clean(t['unit_' + p.unit] || p.unit), X[3], y); right(nf(lvNum(p.price)), X[4]); right(money(lvAmount(p)), X[5]);
      y += ln.length * 4.2 + 2;
    });
    ensure(8); doc.setFont('helvetica', 'bold'); right(`${t.lvSecSum} ${String(si + 1).padStart(2, '0')}: ${money(list.reduce((a, p) => a + lvAmount(p), 0))}`, 196);
    doc.setDrawColor(226, 232, 240); doc.line(M, y + 2, 196, y + 2); y += 8;
  });
  const v = vatSplit(lvTotal(project));
  ensure(22); doc.setFontSize(10);
  [[t.vatNet, v.net], [t.vatLine.replace('{rate}', v.rate), v.vat], [t.vatGross, v.gross]].forEach(([k, n], i) => { doc.setFont('helvetica', i === 2 ? 'bold' : 'normal'); right(`${k}: ${money(n)}`, 196); y += 6; });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(120); doc.text(clean(APP_INFO.name), M, 290);
  doc.save(`${safeName(project.title)}_LV.pdf`);
}
